<script lang="ts">
  export let showSteps: boolean;
  import StepProgress from './StepProgress.svelte';
  import HeroButton from './HeroButton.svelte';
  import { createEventDispatcher } from 'svelte';
  import {
    UserIcon,
    MailAltIcon,
    PhoneAltFilledIcon,
    ZipCodeIcon,
  } from '../../../components/icons';
  import type { Form5 } from '../../types';
  import {
    formatPhoneNumber,
    isValidPhoneNumber,
  } from 'core-components/helpers';

  const dispatch = createEventDispatcher();

  export let pollsSum: number;
  export let data: Form5;
  export let title: string;

  let name = '';
  let email = '';
  let phone = '';
  let zipCode = '';
  let message = '';

  let errorName = false;
  let errorEmail = false;
  let errorEmailValid = false;
  let errorPhone = false;
  let errorPhoneValid = false;
  let errorZip = false;
  let errorMessage = false;

  function isValidEmail(val: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
  }

  function isValidZip(zip: string) {
    const zipRegex = /^\d{5}(-\d{4})?$/;
    const postalCodeRegex = /^[A-Za-z]\d[A-Za-z][ ]?\d[A-Za-z]\d$/;
    return zipRegex.test(zip) || postalCodeRegex.test(zip);
  }

  function handlePhoneChange() {
    phone = formatPhoneNumber(phone);
  }

  function validate() {
    errorName = name.trim().length === 0;
    errorEmail = data.emailRequired ? email.trim().length === 0 : false;
    errorEmailValid = data.emailRequired
      ? !errorEmail && !isValidEmail(email)
      : false;
    errorPhone = data.phoneRequired ? phone.trim().length === 0 : false;
    errorPhoneValid = data.phoneRequired
      ? !errorPhone && !isValidPhoneNumber(phone)
      : false;
    errorZip = data.zipCodeRequired
      ? zipCode.trim().length === 0 || !isValidZip(zipCode)
      : false;
    errorMessage = data.commentsRequired ? message.trim().length === 0 : false;

    return !(
      errorName ||
      (data.emailRequired && (errorEmail || errorEmailValid)) ||
      (data.phoneRequired && (errorPhone || errorPhoneValid)) ||
      (data.zipCodeRequired && errorZip) ||
      (data.commentsRequired && errorMessage)
    );
  }

  function handleNext(event: SubmitEvent) {
    if (validate()) {
      dispatch('next', {
        formData: {
          name,
          email,
          phone,
          zipCode,
          message,
        },
        form: event.target,
      });
    }
  }
</script>

<div
  class="flex h-full w-full flex-col rounded-3xl bg-[#B3D18433] px-4 py-5 md:px-10 md:py-10"
>
  <StepProgress {pollsSum} {showSteps} {title} step={pollsSum + 1} />

  <form class="w-full space-y-3" on:submit|preventDefault={handleNext}>
    <div>
      <div class="relative">
        <input
          class="w-full rounded border py-4 pl-10 pr-3"
          placeholder="Full Name"
          bind:value={name}
        />
        <div
          class="absolute left-4 top-1/2 flex -translate-y-1/2 items-center justify-center"
        >
          <UserIcon class="h-4 w-4" />
        </div>
      </div>
      {#if errorName}
        <p class="mt-1 pl-4 text-xs !text-red-500">Name is required</p>
      {/if}
    </div>

    {#if data.emailRequired}
      <div>
        <div class="relative">
          <input
            class="w-full rounded border py-4 pl-10 pr-3"
            placeholder="Email"
            bind:value={email}
          />
          <div
            class="absolute left-4 top-1/2 flex -translate-y-1/2 items-center justify-center"
          >
            <MailAltIcon class="h-4 w-4" />
          </div>
        </div>
        {#if errorEmail}
          <p class="mt-1 pl-4 text-xs !text-red-500">Email is required</p>
        {:else if errorEmailValid}
          <p class="mt-1 pl-4 text-xs !text-red-500">Email is not valid</p>
        {/if}
      </div>
    {/if}

    {#if data.phoneRequired}
      <div>
        <div class="relative">
          <input
            class="w-full rounded border py-4 pl-10 pr-3"
            placeholder="Phone"
            bind:value={phone}
            on:input={handlePhoneChange}
          />
          <div
            class="absolute left-4 top-1/2 flex -translate-y-1/2 items-center justify-center"
          >
            <PhoneAltFilledIcon class="h-4 w-4" />
          </div>
        </div>
        {#if errorPhone}
          <p class="mt-1 pl-4 text-xs !text-red-500">Phone is required</p>
        {:else if errorPhoneValid}
          <p class="mt-1 pl-4 text-xs !text-red-500">Phone is not valid</p>
        {/if}
      </div>
    {/if}

    <div>
      <div class="relative">
        <input
          class="w-full rounded border py-4 pl-10 pr-3"
          placeholder="Zip Code"
          bind:value={zipCode}
        />
        <div
          class="absolute left-4 top-1/2 flex -translate-y-1/2 items-center justify-center"
        >
          <ZipCodeIcon class="h-4 w-4" />
        </div>
      </div>
      {#if errorZip}
        <p class="mt-1 pl-4 text-xs !text-red-500">
          Zip code is required and must be valid
        </p>
      {/if}
    </div>

    {#if data.commentsRequired}
      <div>
        <div class="relative">
          <textarea
            class="w-full rounded border p-4 pb-8"
            placeholder="Message"
            bind:value={message}
            rows="3"
            maxlength="500"
          ></textarea>
          <p
            class="absolute bottom-4 right-4 text-sm leading-normal text-[#787E87]"
          >
            {message.length}/500
          </p>
        </div>
        {#if errorMessage}
          <p class="mt-1 text-xs !text-red-500">Message is required</p>
        {/if}
      </div>
    {/if}

    <div class="mt-4 md:my-8">
      <HeroButton text="Next" type="submit" />
    </div>
    <p
      class="hidden text-center text-xs !font-medium leading-normal !text-[#111111A3] md:block"
    >
      Zero Spam Policy
    </p>
  </form>
</div>
